import { action, observable, computed, makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "./rootStore";
import { CountriesDto, CountryDto, RenewPurchaseDto, RenewSubscriptionDto } from "../models/dtoTypes";
import { checkDiscountCode, fetchCountries, getPaymentMode, renewPurchasePost, restoreSubscriptionPost, updatePaymentMethodPost } from "../api/orderService";
import { fetchRenewal, fetchRenewalByActivationCodeEncoded } from "../api/subscriptionService";

//sessionStorage key: the renew page URL a checkout was started from (read by /renew/cancel)
export const RENEW_RETURN_URL_KEY = 'dcr.renewReturnUrl';

export default class RenewStore {
    rootStore: RootStore | undefined;

    @observable loading: boolean = false;
    @observable countries: CountryDto[] = [];
    subscriptionId: number | undefined;
    @observable isNotExpiredYet: boolean = false;
    @observable pricePerUser: number = 0;
    @observable qnt: number = 1;
    @observable renewalDiscount: number = 0;
    @observable endDate: string = '';
    //which checkout to present: '' (loading), 'stripe' or 'maintenance' (decided server-side)
    @observable paymentProvider: string = '';
    //Stripe as merchant of record: hide the manual invoice form (Stripe issues the invoice)
    @observable managedPayments: boolean = false;
    //'GBP' for non-EU visitors, 'EUR' for EU visitors (from the renew endpoint)
    @observable currency: string = 'GBP';

    //server-resolved renewal mode - the page renders exactly this, never inferring
    @observable renewalMode: string = '';
    @observable billingPeriodMonths: number = 12;
    @observable nextChargeDate: string = '';
    hostedInvoiceUrl: string = '';
    //unused-time credit for an early legacy renewal (server-computed; days=0 hides the row)
    @observable unusedTimeCreditDays: number = 0;
    @observable unusedTimeCreditAmount: number = 0;
    //the server answered 401: a Stripe-linked subscription shows details to its signed-in owner only
    @observable signInRequired: boolean = false;
    //resume / card-update in flight + outcome
    @observable actionBusy: boolean = false;
    @observable actionError: string = '';

    //remembered so the page can refetch after the customer signs in
    private lastSubscriptionCode: string = '';
    private lastActivationCode: string = '';

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this);

        getPaymentMode().then((mode) => {
            runInAction(() => { this.paymentProvider = mode.provider; this.managedPayments = mode.managedPayments; });
        }).catch(() => {
            //if we cannot determine the region, fail safe to the maintenance page
            runInAction(() => { this.paymentProvider = 'maintenance'; });
        });
    }

    @action private applyRenewalDto(response: RenewSubscriptionDto) {
        this.subscriptionId = response.subscriptionId;
        this.qnt = response.qnt;
        this.pricePerUser = response.pricePerUser;
        this.isNotExpiredYet = response.isNotExpiredYet;
        this.renewalDiscount = response.renewalDiscount;
        this.endDate = response.endDate;
        this.currency = response.currency ?? 'GBP';
        this.renewalMode = response.renewalMode ?? 'legacy';
        this.billingPeriodMonths = response.billingPeriodMonths === 1 ? 1 : 12;
        this.nextChargeDate = response.nextChargeDate ?? '';
        this.hostedInvoiceUrl = response.hostedInvoiceUrl ?? '';
        this.unusedTimeCreditDays = response.unusedTimeCreditDays ?? 0;
        this.unusedTimeCreditAmount = response.unusedTimeCreditAmount ?? 0;
        this.signInRequired = false;
        this.actionError = '';
    }

    @action private handleRenewalFetchError(error: any, redirectHomeOnFailure: boolean) {
        //401 = Stripe-linked subscription, sign in first; the page shows the login prompt and
        //refetches once the customer is signed in
        if (error?.response?.status === 401) {
            this.signInRequired = true;
            return;
        }
        console.error('Failed to fetch renewal:', error);
        if (redirectHomeOnFailure) window.location.href = '/';
    }

    //re-runs whichever fetch loaded the page (after sign-in, or after an action)
    @action refetch() {
        if (this.lastSubscriptionCode) this.setSubscription(this.lastSubscriptionCode);
        else if (this.lastActivationCode) this.setSubscriptionByActivationCode(this.lastActivationCode);
    }

    @action setSubscription(subscriptionCode: string) {
        this.loading = true;
        this.lastSubscriptionCode = subscriptionCode;
        fetchRenewal(subscriptionCode).then((response) => {
            runInAction(() => this.applyRenewalDto(response));
            return response;
        })
            .catch((error) => {
                runInAction(() => this.handleRenewalFetchError(error, true));
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });

        fetchCountries().then((response) => {
            runInAction(() => {
                if (response.detectedCountryCode !== undefined && response.detectedCountryCode !== null) {
                    this.invoiceCountryIsoCode = response.detectedCountryCode;
                } else {
                    this.invoiceCountryIsoCode = 'GB';
                }
                this.countries = response.countries;
            });
        }).catch((error) => {
            console.error('Failed to fetch countries:', error);
        });
    }

    @action setSubscriptionByActivationCode(activationCodeEncoded: string) {
        this.loading = true;
        this.lastActivationCode = activationCodeEncoded;
        fetchRenewalByActivationCodeEncoded(activationCodeEncoded).then((response) => {
            runInAction(() => this.applyRenewalDto(response));
            return response;
        })
            .catch((error) => {
                runInAction(() => this.handleRenewalFetchError(error, false));
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    //#region live-subscription actions (resume / pay invoice / update card)

    @action resumeAutoRenew() {
        if (!this.subscriptionId || this.actionBusy) return;
        this.actionBusy = true;
        this.actionError = '';
        restoreSubscriptionPost(this.subscriptionId).then(() => {
            runInAction(() => { this.renewalMode = 'autoRenewing'; });
            this.refetch();
        }).catch((error) => {
            runInAction(() => {
                console.error('Failed to resume auto-renewal:', error);
                this.actionError = 'Could not resume auto-renewal. Please try again later.';
            });
        }).finally(() => runInAction(() => { this.actionBusy = false; }));
    }

    @action payInvoice() {
        //Stripe's hosted page for the open (failed) invoice - server-provided, owner-only
        if (this.hostedInvoiceUrl) {
            window.location.href = this.hostedInvoiceUrl;
            return;
        }
        //never a silent no-op: the invoice link can be missing when Stripe hasn't reported the
        //failed invoice yet or the page is stale
        this.actionError = "The invoice link isn't available right now. Please refresh the page or update your payment details.";
    }

    @action updatePaymentMethod() {
        if (!this.subscriptionId || this.actionBusy) return;
        this.actionBusy = true;
        this.actionError = '';
        //the portal URL is one-time and short-lived: fetched per click, navigated to at once
        updatePaymentMethodPost(this.subscriptionId).then((url) => {
            if (url) window.location.href = url;
            else runInAction(() => { this.actionError = 'Payment details cannot be updated right now.'; });
        }).catch((error) => {
            runInAction(() => {
                console.error('Failed to open the card-update page:', error);
                this.actionError = 'Payment details cannot be updated right now. Please try again later.';
            });
        }).finally(() => runInAction(() => { this.actionBusy = false; }));
    }

    //#endregion

    //#region Prices and rates

    @computed get pricePerUserString() {
        return this.pricePerUser.toFixed(2);
    }

    get PricePerMonthString(): string {
        //monthly plans price per month directly; yearly shows the per-month equivalent
        const priceM = this.billingPeriodMonths === 1 ? this.pricePerUser : this.pricePerUser / 12;
        return priceM.toFixed(2);
    }

    @computed get isMonthly() {
        return this.billingPeriodMonths === 1;
    }

    @computed get renewalDiscountString() {
        return this.renewalDiscount.toFixed(0);
    }

    @computed get earlyRenewalDiscountApplied() {
        return (this.renewalDiscount > 0) && !this.isDiscountCodeValid;
    }
    @computed get renewalDiscountAmountString() {
        return (this.total * this.renewalDiscount / 100).toFixed(2);
    }

    @computed get total() {
        return this.qnt * this.pricePerUser;
    }
    @computed get totalString() {
        return this.total.toFixed(2);
    }


    // @computed get anyDiscountApplied() {
    //     return this.totalDiscount > 0;
    // }

    @computed get discountCodeAmountString() {
        return ((this.isDiscountCodeValid ? this.discountCodePercentage : 0) * this.total / 100).toFixed(2);
    }


    @computed get totalDiscount() {
        return (this.isDiscountCodeValid ? this.discountCodePercentage : this.renewalDiscount) * this.total / 100;
    }


    //unused-time credit is a refund of surrendered paid days: it comes off the total on top of
    //any incentive discount, and the amount is the exact server-computed value (never derived here)
    @computed get hasUnusedTimeCredit() {
        return this.unusedTimeCreditDays > 0 && this.unusedTimeCreditAmount > 0;
    }
    @computed get unusedTimeCreditString() {
        return this.unusedTimeCreditAmount.toFixed(2);
    }

    @computed get totalAfterDiscount() {
        return this.total - this.totalDiscount - this.unusedTimeCreditAmount;
    }
    @computed get totalAfterDiscountString() {
        return this.totalAfterDiscount.toFixed(2);
    }

    //computer property for the price in USD and EUR based on the current exchange rates as string with 2 decimal places
    @computed get USDPriceString(): string {
        var result: string = "";
        if (this.rootStore?.ratesLoaded) {
            result = (this.totalAfterDiscount * this.rootStore.rates["GBPUSD"]).toFixed(0);
        }
        return result;

    }
    @computed get EURPriceString(): string {
        var result: string = "";
        if (this.rootStore?.ratesLoaded) {
            result = (this.totalAfterDiscount * this.rootStore.rates["GBPEUR"]).toFixed(0);
        }
        return result;
    }
    //#endregion

    //#region Discount code
    @observable iHaveDiscount: boolean = false;
    @action setIHaveDiscount(value: boolean) {
        this.iHaveDiscount = value;
    }
    @observable discountCode: string = '';
    @observable showCheckingDiscountSpinner: boolean = false;
    @observable discountCodePercentage: number = 0;
    @computed get discountPercentageString() {
        return this.discountCodePercentage.toFixed(0);
    }

    @action setDiscountCode(value: string) {
        this.discountError = '';
        this.discountCodePercentage = 0;

        //trimming and uppercasing the discount code, then checking its length
        let dCode: string = value.trim().toUpperCase();
        this.discountCode = dCode;
        if (dCode.length !== 6) {
            this.showCheckingDiscountSpinner = false;
            return;
        }
        this.discountCode = dCode;
        this.showCheckingDiscountSpinner = true;

        checkDiscountCode(dCode).then((response) => {
            runInAction(() => {
                this.showCheckingDiscountSpinner = false;
                this.isDiscountCodeValid = response.isDiscountCodeValid;
                if (response.isDiscountCodeValid) {
                    this.discountCodePercentage = response.discountPercentage;
                }
                else {
                    this.discountCodePercentage = 0;
                }

                this.discountError = response.discountCodeError;
            });
        }).catch((error) => {
            this.showCheckingDiscountSpinner = false;
            this.discountError = error.message;
        });

    }

    @observable discountError: string = '';

    @observable isDiscountCodeValid: boolean = false;

    //#endregion



    //#region invoice properties
    @observable isInvoiceNeeded: boolean = false;
    @action setInvoiceNeeded(value: boolean) {
        this.isInvoiceNeeded = value;
    }

    @observable invoiceRefNumber: string = '';
    @action setInvoiceRefNumber(value: string) {
        this.invoiceRefNumber = value;
    }

    fieldStyleNormal: string = "";
    fieldStyleError: string = "field-err-input";

    @observable invoiceBusinessNameTouched: boolean = false;
    @observable invoiceBusinessName: string = '';
    @action setInvoiceBusinessName(value: string) {
        this.invoiceBusinessName = value;
        this.invoiceBusinessNameTouched = true;
    }
    @computed get invoiceBusinessNameFieldStyle() {
        if (this.invoiceBusinessNameTouched && this.invoiceBusinessName.trim() === '') {
            return this.fieldStyleError;
        }
        return this.fieldStyleNormal;
    }

    @observable invoiceAddress: string = '';
    @action setInvoiceAddress(value: string) {
        this.invoiceAddress = value;
        this.invoiceAddressTouched = true;
    }
    @observable invoiceAddressTouched: boolean = false;
    @computed get invoiceAddressFieldStyle() {
        if (this.invoiceAddressTouched && this.invoiceAddress.trim() === '') {
            return this.fieldStyleError;
        }
        return this.fieldStyleNormal;
    }

    @observable invoiceCountryIsoCodeTouched: boolean = false;
    @observable invoiceCountryIsoCode: string = '';
    @action setInvoiceCountryIsoCode(value: string) {
        this.invoiceCountryIsoCode = value;
        this.invoiceCountryIsoCodeTouched = true;
    }
    @computed get invoiceCountryIsoCodeFieldStyle() {
        if (this.invoiceCountryIsoCodeTouched && this.invoiceCountryIsoCode.trim() === '') {
            return this.fieldStyleError;
        }
        return this.fieldStyleNormal;
    }

    @computed get isInvoiceDataValid() {
        if (this.isInvoiceNeeded) {
            if (this.invoiceBusinessName === '') {
                return false;
            }
            if (this.invoiceAddress === '') {
                return false;
            }
            if (this.invoiceCountryIsoCode === '') {
                return false;
            }
        }
        return true;
    }
    //#endregion

    // proccesing order properties
    @observable isCheckoutClicked: boolean = false;
    @observable PurchaseError: string = '';
    @computed get isOrderValid() {
        if (!this.isInvoiceDataValid) {
            return false;
        }
        return this.qnt > 0;
    }

    @action HandleCheckoutClicked() {
        this.isCheckoutClicked = true;
        this.invoiceBusinessNameTouched = true;
        this.invoiceAddressTouched = true;
        this.invoiceCountryIsoCodeTouched = true;
        if (!this.isInvoiceDataValid) {
            this.isCheckoutClicked = false;
            return;
        }

        try {
            this.PurchaseError = '';
            var renewPurchaseDto: RenewPurchaseDto = {
                Url: window.location.origin,
                SubscriptionId: this.subscriptionId ? this.subscriptionId : 0,
                DiscountCode: this.iHaveDiscount ? this.discountCode : '',
                //only send invoice details when the customer ticked "I need an invoice" - otherwise
                //the backend auto-generates one from these fields, and stale values must never leak
                Name: this.isInvoiceNeeded ? this.invoiceBusinessName : '',
                Address: this.isInvoiceNeeded ? this.invoiceAddress : '',
                BuyersRefNumber: this.isInvoiceNeeded ? this.invoiceRefNumber : '',
                CountryIsoCode: this.invoiceCountryIsoCode,
                InvoiceNeeded: this.isInvoiceNeeded,
                BuyersVatNumber: ''
            };

            //Stripe hosted checkout - redirect; Stripe returns to /renew/success/{paymentId}
            renewPurchasePost(renewPurchaseDto).then((redirectUrl) => {
                if (!redirectUrl) {
                    runInAction(() => {
                        this.PurchaseError = "Something went wrong, please try again later.";
                        this.isCheckoutClicked = false;
                    });
                    return;
                }
                //remembered so /renew/cancel can bring the customer back to THIS renew page
                //(not to a fresh purchase); best-effort - storage may be unavailable
                try { sessionStorage.setItem(RENEW_RETURN_URL_KEY, window.location.href); } catch { }
                window.location.href = redirectUrl;
            }).catch((error) => {
                runInAction(() => {
                    console.error('Failed to purchase:', error);
                    this.PurchaseError = "Something went wrong: " + error.message;
                    this.isCheckoutClicked = false;
                });
            });
        } catch (error) {
            runInAction(() => {
                console.error('Failed to create purchaseDto:', error);
                this.PurchaseError = "Something went wrong: ";
                this.isCheckoutClicked = false;
            });
            return;
        }

    }
};

